import { createClient as createElectronClient } from '@openphone/desktop-client'
import {
  getLogSink,
  initObservability,
  logError,
  trackCustomAction,
} from '@openphone/quo-observability'
import {
  configure as configureLogger,
  getConsoleSink,
} from '@openphone/quo-observability/logger'
import { createRoot } from 'react-dom/client'
import { RelayEnvironmentProvider } from 'react-relay'
import { RouterProvider, createBrowserRouter } from 'react-router-dom'

import App from './app/App'
import AppStore from './app/AppStore'
import { AppContext } from './app/context'
import { initPreloadErrorListener } from './app/initPreloadErrorListener'
import appConfig, { isMac, isWindows } from './config'
import { createRelayEnvironment } from './graphql/client'
import { extendDayJs } from './lib/dayjs'
import Service from './service'
import Worker from './service/worker?worker&url'

import { AnalyticsProvider } from '@odyssey/analytics/AnalyticsContext'
import { AppContext as OdysseyAppContext } from '@odyssey/contexts/AppContext/AppContext'
import { HttpClientContext } from '@odyssey/contexts/HttpClientContext/HttpClientContext'
import { ExperimentProvider } from '@odyssey/experiment/ExperimentContext'
import { FlagsContext } from '@odyssey/feature-flags/FlagsContext'
import UpdateContextBridgeProvider from '@odyssey/legacy/UpdateContextBridgeProvider/UpdateContextBridgeProvider'
import { getRootLogger } from '@odyssey/logging/logger'

extendDayJs()

async function loadApp() {
  const client = createElectronClient() ?? null

  await configureLogger({
    sinks: { console: getConsoleSink(), datadog: getLogSink() },
    loggers: [
      {
        category: '@openphone/web',
        lowestLevel: appConfig.ENV === 'production' ? 'info' : 'debug',
        sinks: ['console'],
      },
      {
        category: [],
        lowestLevel: 'warning',
        sinks: ['datadog'],
      },
    ],
  })

  const service = new Service(
    {
      workerName: Worker,
    },
    client,
  )

  // Page-scoped: `loadApp` runs exactly once per page load, so we intentionally
  // drop the disposer.
  initPreloadErrorListener({ voiceService: service.voice })

  const relayEnvironment = createRelayEnvironment({
    httpClient: service.transport.client,
    getViewerId: () => service.user.current?.id ?? null,
  })

  const prevOnError = window.onerror
  window.onerror = function (message: string | Event, ...args: any[]) {
    // Benign error that can be ignored https://stackoverflow.com/a/50387233
    if (message === 'ResizeObserver loop limit exceeded') {
      return
    }

    getRootLogger().error(typeof message === 'string' ? message : 'Unknown Error', {
      args,
    })

    if (prevOnError) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-argument -- FIXME: Fix this ESLint violation!
      return prevOnError(message, ...args)
    }

    return false
  }

  await service.flags.waitUntilReady()

  const { flags } = service.flags
  const desktopPlatform = isMac
    ? 'desktop-mac'
    : isWindows
      ? 'desktop-windows'
      : 'desktop-linux'

  initObservability({
    clientToken: appConfig.DATADOG_CLIENT_TOKEN,
    applicationId: appConfig.DATADOG_APP_ID,
    service: appConfig.DATADOG_SERVICE,
    env: appConfig.LOG_ENV,
    version: appConfig.FULL_VERSION,
    sessionSampleRate: flags.webDataDogSessionSampleRate,
    sessionReplaySampleRate: flags.webDataDogSessionReplaySampleRate,
    traceSampleRate: flags.webDataDogTraceSampleRate,
    profilingSampleRate: flags.webDataDogProfilingSampleRate,
    defaultPrivacyLevel: import.meta.env.PROD ? 'mask-user-input' : 'allow',
    allowedTracingUrls: [
      appConfig.AI_SERVICE_URL,
      appConfig.AUTH_SERVICE_URL,
      appConfig.ACCOUNT_SERVICE_URL,
      appConfig.BILLING_SERVICE_URL,
      appConfig.COMMUNICATION_SERVICE_URL,
      appConfig.CONTACT_SERVICE_URL,
      appConfig.INTEGRATION_SERVICE_URL,
      appConfig.MESSAGING_SERVICE_URL,
      appConfig.REPORT_SERVICE_URL,
      appConfig.VOICE_SERVICE_URL,
      appConfig.WORKFLOW_SERVICE_URL,
      appConfig.SEARCH_SERVICE_URL,
      appConfig.WEBSOCKET_SERVICE_URL,
      appConfig.API_SERVICE_URL,
      /\/api\/graphql/,
    ],
    globalContext: {
      platform: client?.version ? desktopPlatform : 'browser',
      desktop: client?.version ? { version: client.version } : undefined,
    },
  })

  // Redirect from my.openphone.com to my.quo.com if the feature flag is enabled
  if (service.flags.flags.webRedirectFromOpenPhoneToQuo) {
    const logger = getRootLogger().getChild('QuoRedirect')

    try {
      const url = new URL(window.location.href)
      const isOnOpenPhoneDomain =
        url.hostname === 'my.openphone.com' || url.hostname === 'my.openphone.co'

      if (isOnOpenPhoneDomain) {
        url.hostname = 'my.quo.com'

        // Clear service worker, and app storage before redirecting
        await service.storage.clearAll().catch((error) => {
          logger.warn('Error clearing storage during redirect from OpenPhone to Quo', {
            error,
          })
        })
        const registrations = await navigator.serviceWorker
          .getRegistrations()
          .catch(() => [] as readonly ServiceWorkerRegistration[])
        await Promise.all(
          registrations.map((registration) => registration.unregister()),
        ).catch((error) => {
          logger.warn(
            'Error unregistering a service worker during redirect from OpenPhone to Quo',
            {
              error,
            },
          )
        })

        window.__markAppBootstrapped?.()
        window.location.href = url.toString()
        return
      }
    } catch (error) {
      logger.error('Error during redirect from OpenPhone to Quo', { error })
    }
  }

  // Handle token override (used for impersonation and tests).
  // Must run before auth init to clear any prior session state.
  const params = new URLSearchParams(window.location.search)
  const accessTokenOverride =
    params.get('access_token_override') || params.get('session_id_token')
  if (accessTokenOverride) {
    await service.authV2.applyTokenOverrideFromUrl(
      accessTokenOverride,
      params.has('impersonated_user_id'),
    )

    params.delete('access_token_override')
    params.delete('impersonated_user_id')
    // legacy impersonation param
    params.delete('session_id_token')
    const cleanUrl = new URL(window.location.href)
    cleanUrl.search = params.toString()
    window.history.replaceState(null, '', cleanUrl.toString())
  }

  const router = createBrowserRouter([
    {
      path: '*',
      element: <App />,
    },
  ])

  const store = new AppStore(client, router, service)

  const element = document.getElementById('root')

  if (!element) {
    throw new Error('Could not find root element')
  }

  const root = createRoot(element)

  await service.authV2
    .init()
    .then(() => {
      if (service.authV2.isSessionAboutToExpire) {
        trackCustomAction({
          type: 'logging-out-user-session-about-to-expire',
        })
        // Fire-and-forget: clearAllAndRestart triggers a page reload, so
        // nothing after this point is expected to run. Safe as long as no
        // additional work is added to this .then() chain.
        service.clearAllAndRestart({ reason: 'session-about-to-expire' }).catch(logError)
      }
    })
    .catch(logError)

  root.render(
    <AppContext.Provider value={store}>
      <OdysseyAppContext.Provider value={{ reset: () => store.reset() }}>
        <FlagsContext.Provider
          value={{
            getFlag: (flag) => service.flags.flags[flag],
            subscribeToFlag: service.flags.subscribeToFlag,
          }}
        >
          <AnalyticsProvider
            isAuthenticated={() => service.authV2.isAuthenticated}
            router={(url) => router.navigate(url)}
          >
            <ExperimentProvider>
              <UpdateContextBridgeProvider updateController={store.update}>
                <HttpClientContext.Provider value={service.transport.client}>
                  <RelayEnvironmentProvider environment={relayEnvironment}>
                    <RouterProvider
                      router={router}
                      future={{ v7_startTransition: true }}
                    />
                  </RelayEnvironmentProvider>
                </HttpClientContext.Provider>
              </UpdateContextBridgeProvider>
            </ExperimentProvider>
          </AnalyticsProvider>
        </FlagsContext.Provider>
      </OdysseyAppContext.Provider>
    </AppContext.Provider>,
  )

  window.__markAppBootstrapped?.()
}

// eslint-disable-next-line @typescript-eslint/no-floating-promises -- UXP-3744 - Fix Promise-related ESLint issues
loadApp()
